import React, { useState, useMemo } from 'react';
import { Container, Row, Col, Table, Card, Button, Spinner, Badge, Form, Nav, Tab, Alert } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useStats } from './contexts/StatsContext';
import { useAuth } from './contexts/AuthContext';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
} from '@tanstack/react-table';

// Stat Card Component
function StatCard({ title, value, variant = 'primary' }) {
  return (
    <Card className="mb-3 h-100">
      <Card.Body>
        <Card.Subtitle className="mb-2 text-muted small">{title}</Card.Subtitle>
        <Card.Title className={`text-${variant} mb-0`}>
          {value?.toLocaleString() || 'Loading...'}
        </Card.Title>
      </Card.Body>
    </Card>
  );
}

function Statistics() {
  const { stats, prefixData, orderData, yearData, ledBatchData, tileTypesInfo4Data, isLoading } = useStats();
  const { user } = useAuth();
  const [expandedRows, setExpandedRows] = useState({});
  const [tileFilter, setTileFilter] = useState('');
  const [orderFilter, setOrderFilter] = useState('');
  const [yearFilter, setYearFilter] = useState('');
  const [ledBatchFilter, setLedBatchFilter] = useState('');
  const [tileTypesInfo4Filter, setTileTypesInfo4Filter] = useState('');

  // Check if user has access to statistics page
  // Only Azure SSO users or users with @roevisual.com email can access
  const hasAccess = useMemo(() => {
    if (!user) return false;

    const email = user.email?.toLowerCase() || '';
    const isRoeVisualEmail = email.endsWith('@roevisual.com');

    // Check if user authenticated via Azure SSO
    const isAzureUser = user.app_metadata?.provider === 'azure' ||
                        user.identities?.some(identity => identity.provider === 'azure');

    return isRoeVisualEmail || isAzureUser;
  }, [user]);

  const toggleRowExpansion = (index) => {
    setExpandedRows(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  // Tile Prefixes Table Configuration
  const tileColumns = useMemo(
    () => [
      {
        accessorKey: 'tile_sn_prefix',
        header: 'Tile SN Prefix',
        cell: info => <strong>{info.getValue()}</strong>,
      },
      {
        accessorKey: 'unique_order_count',
        header: 'Total Orders',
        cell: info => info.getValue(),
      },
      {
        accessorKey: 'order_numbers',
        header: 'Orders',
        enableSorting: false,
        cell: ({ row }) => {
          const orders = row.original.order_numbers;
          const rowKey = `prefix-${row.index}`;
          const isExpanded = expandedRows[rowKey];

          // If only 1 order, just show it
          if (orders.length === 1) {
            return (
              <Link to={`/search/${orders[0]}`}>{orders[0]}</Link>
            );
          }

          // If multiple orders, show expand/collapse button
          return (
            <div>
              {isExpanded && (
                <>
                  {orders.map(orderNum => (
                    <div key={orderNum}>
                      <Link to={`/search/${orderNum}`}>{orderNum}</Link>
                    </div>
                  ))}
                </>
              )}
              <Button
                variant="link"
                size="sm"
                onClick={() => toggleRowExpansion(rowKey)}
                className="p-0"
              >
                {isExpanded ? (
                  <>Show less</>
                ) : (
                  <>
                    Show {orders.length} orders...{' '}
                    <Badge bg="secondary">{orders.length}</Badge>
                  </>
                )}
              </Button>
            </div>
          );
        },
      },
      {
        accessorKey: 'unique_tile_count',
        header: 'Total Panels',
        cell: info => info.getValue().toLocaleString(),
      },
      {
        accessorKey: 'total_count',
        header: 'Total Modules',
        cell: info => info.getValue().toLocaleString(),
      },
    ],
    [expandedRows]
  );

  // Orders Table Configuration
  const orderColumns = useMemo(
    () => [
      {
        accessorKey: 'order_num',
        header: 'Order Number',
        cell: ({ row }) => (
          <Link to={`/search/${row.original.order_num}`}>
            <strong>{row.original.order_num}</strong>
          </Link>
        ),
      },
      {
        accessorKey: 'tile_sn_prefixes',
        header: 'Panel Type',
        cell: ({ row }) => row.original.tile_sn_prefixes?.[0] || '',
      },
      {
        accessorKey: 'unique_tile_count',
        header: 'Total Panels',
        cell: info => info.getValue().toLocaleString(),
      },
      {
        accessorKey: 'total_count',
        header: 'Total Modules',
        cell: info => info.getValue().toLocaleString(),
      },
    ],
    []
  );

  // Year Statistics Table Configuration
  const yearColumns = useMemo(
    () => [
      {
        accessorKey: 'production_year',
        header: 'Production Year',
        cell: info => <strong>{info.getValue()}</strong>,
      },
      {
        accessorKey: 'unique_order_count',
        header: 'Total Orders',
        cell: info => info.getValue().toLocaleString(),
      },
      {
        accessorKey: 'unique_tile_count',
        header: 'Total Panels',
        cell: info => info.getValue().toLocaleString(),
      },
      {
        accessorKey: 'total_module_count',
        header: 'Total Modules',
        cell: info => info.getValue().toLocaleString(),
      },
    ],
    []
  );

  // LED Batch Statistics Table Configuration
  const ledBatchColumns = useMemo(
    () => [
      {
        accessorKey: 'led_batch',
        header: 'LED Batch',
        cell: info => <strong>{info.getValue()}</strong>,
      },
      {
        accessorKey: 'tile_count',
        header: 'Tile Count',
        cell: info => info.getValue()?.toLocaleString() || '0',
      },
      {
        accessorKey: 'module_count',
        header: 'Module Count',
        cell: info => info.getValue()?.toLocaleString() || '0',
      },
      {
        accessorKey: 'order_count',
        header: 'Order Count',
        cell: info => info.getValue()?.toLocaleString() || '0',
      },
      {
        accessorKey: 'orders',
        header: 'Orders',
        enableSorting: false,
        cell: ({ row }) => {
          const ordersString = row.original.orders;
          if (!ordersString) return null;

          const orders = ordersString.split(', ').map(o => o.trim()).filter(o => o);
          const rowKey = `led-batch-${row.index}`;
          const isExpanded = expandedRows[rowKey];

          // If only 1 order, just show it
          if (orders.length === 1) {
            return (
              <Link to={`/search/${orders[0]}`}>{orders[0]}</Link>
            );
          }

          // If multiple orders, show expand/collapse button
          return (
            <div>
              {isExpanded && (
                <>
                  {orders.map(orderNum => (
                    <div key={orderNum}>
                      <Link to={`/search/${orderNum}`}>{orderNum}</Link>
                    </div>
                  ))}
                </>
              )}
              <Button
                variant="link"
                size="sm"
                onClick={() => toggleRowExpansion(rowKey)}
                className="p-0"
              >
                {isExpanded ? (
                  <>Show less</>
                ) : (
                  <>
                    Show {orders.length} orders...{' '}
                    <Badge bg="secondary">{orders.length}</Badge>
                  </>
                )}
              </Button>
            </div>
          );
        },
      },
    ],
    [expandedRows]
  );

  // Tile Types Info4 Table Configuration
  const tileTypesInfo4Columns = useMemo(
    () => [
      {
        accessorKey: 'tile_type',
        header: 'Tile Type',
        cell: info => <strong>{info.getValue()}</strong>,
      },
      {
        accessorKey: 'unique_order_count',
        header: 'Total Orders',
        cell: info => info.getValue(),
      },
      {
        accessorKey: 'order_numbers',
        header: 'Orders',
        enableSorting: false,
        cell: ({ row }) => {
          const orders = row.original.order_numbers;
          const rowKey = `tileinfo4-${row.index}`;
          const isExpanded = expandedRows[rowKey];

          // If only 1 order, just show it
          if (orders.length === 1) {
            return (
              <Link to={`/search/${orders[0]}`}>{orders[0]}</Link>
            );
          }

          // If multiple orders, show expand/collapse button
          return (
            <div>
              {isExpanded && (
                <>
                  {orders.map(orderNum => (
                    <div key={orderNum}>
                      <Link to={`/search/${orderNum}`}>{orderNum}</Link>
                    </div>
                  ))}
                </>
              )}
              <Button
                variant="link"
                size="sm"
                onClick={() => toggleRowExpansion(rowKey)}
                className="p-0"
              >
                {isExpanded ? (
                  <>Show less</>
                ) : (
                  <>
                    Show {orders.length} orders...{' '}
                    <Badge bg="secondary">{orders.length}</Badge>
                  </>
                )}
              </Button>
            </div>
          );
        },
      },
      {
        accessorKey: 'unique_tile_count',
        header: 'Total Panels',
        cell: info => info.getValue().toLocaleString(),
      },
      {
        accessorKey: 'total_count',
        header: 'Total Modules',
        cell: info => info.getValue().toLocaleString(),
      },
    ],
    [expandedRows]
  );

  // Tile Prefixes Table Instance
  const tileTable = useReactTable({
    data: prefixData,
    columns: tileColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    state: {
      globalFilter: tileFilter,
    },
    onGlobalFilterChange: setTileFilter,
    initialState: {
      pagination: {
        pageSize: 25,
      },
    },
  });

  // Orders Table Instance
  const orderTable = useReactTable({
    data: orderData,
    columns: orderColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    state: {
      globalFilter: orderFilter,
    },
    onGlobalFilterChange: setOrderFilter,
    initialState: {
      pagination: {
        pageSize: 50,
      },
    },
  });

  // Year Statistics Table Instance
  const yearTable = useReactTable({
    data: yearData,
    columns: yearColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    state: {
      globalFilter: yearFilter,
    },
    onGlobalFilterChange: setYearFilter,
    initialState: {
      pagination: {
        pageSize: 25,
      },
      sorting: [
        {
          id: 'production_year',
          desc: true,
        },
      ],
    },
  });

  // LED Batch Statistics Table Instance
  const ledBatchTable = useReactTable({
    data: ledBatchData,
    columns: ledBatchColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    state: {
      globalFilter: ledBatchFilter,
    },
    onGlobalFilterChange: setLedBatchFilter,
    initialState: {
      pagination: {
        pageSize: 50,
      },
      sorting: [
        {
          id: 'tile_count',
          desc: true,
        },
      ],
    },
  });

  // Tile Types Info4 Table Instance
  const tileTypesInfo4Table = useReactTable({
    data: tileTypesInfo4Data,
    columns: tileTypesInfo4Columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    state: {
      globalFilter: tileTypesInfo4Filter,
    },
    onGlobalFilterChange: setTileTypesInfo4Filter,
    initialState: {
      pagination: {
        pageSize: 25,
      },
      sorting: [
        {
          id: 'total_count',
          desc: true,
        },
      ],
    },
  });

  // Pagination Controls Component
  const PaginationControls = ({ table }) => (
    <div className="d-flex justify-content-between align-items-center">
      <div className="btn-group">
        <Button
          variant="outline-primary"
          size="sm"
          onClick={() => table.setPageIndex(0)}
          disabled={!table.getCanPreviousPage()}
        >
          {'<<'}
        </Button>
        <Button
          variant="outline-primary"
          size="sm"
          onClick={() => table.previousPage()}
          disabled={!table.getCanPreviousPage()}
        >
          Previous
        </Button>
        <Button
          variant="outline-primary"
          size="sm"
          onClick={() => table.nextPage()}
          disabled={!table.getCanNextPage()}
        >
          Next
        </Button>
        <Button
          variant="outline-primary"
          size="sm"
          onClick={() => table.setPageIndex(table.getPageCount() - 1)}
          disabled={!table.getCanNextPage()}
        >
          {'>>'}
        </Button>
      </div>
      <div className="text-muted small">
        Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
      </div>
      <Form.Group className="d-flex align-items-center mb-0">
        <Form.Label className="mb-0 me-2 small">Rows:</Form.Label>
        <Form.Select
          size="sm"
          style={{ width: '80px' }}
          value={table.getState().pagination.pageSize}
          onChange={e => table.setPageSize(Number(e.target.value))}
        >
          <option value={10}>10</option>
          <option value={25}>25</option>
          <option value={50}>50</option>
          <option value={100}>100</option>
        </Form.Select>
      </Form.Group>
    </div>
  );

  // Show access denied message if user doesn't have permission
  if (!hasAccess) {
    return (
      <Container fluid className="py-4">
        <Alert variant="warning">
          <Alert.Heading>Access Restricted</Alert.Heading>
          <p>
            The Statistics page is for internal use only and is restricted to ROE Visual employees.
          </p>
          <p className="mb-0">
            If you believe you should have access, please contact your administrator at{' '}
            <a href="mailto:ussupport@roevisual.com">ussupport@roevisual.com</a>.
          </p>
        </Alert>
        <div className="mt-3">
          <Link to="/">
            <Button variant="primary">Return to Home</Button>
          </Link>
        </div>
      </Container>
    );
  }

  if (isLoading) {
    return (
      <Container className="text-center py-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1 className="mb-0">Statistics</h1>
        <Badge bg="info" className="px-3 py-2">
          <i className="bi bi-shield-lock-fill me-2"></i>
          For Internal Use Only
        </Badge>
      </div>

      {/* Overview Stat Cards */}
      <Row className="mb-4">
        <Col md={3} sm={6}>
          <StatCard title="Unique Orders" value={stats.uniqueorders} variant="primary" />
        </Col>
        <Col md={3} sm={6}>
          <StatCard title="Unique Tiles" value={stats.uniquetiles} variant="success" />
        </Col>
        <Col md={3} sm={6}>
          <StatCard title="Unique Modules" value={stats.uniquemodules} variant="info" />
        </Col>
        <Col md={3} sm={6}>
          <StatCard title="LED Batches" value={stats.uniqueledbatches} variant="warning" />
        </Col>
      </Row>

      {/* Additional Stats - Collapsible */}
      <Row className="mb-4">
        <Col>
          <details>
            <summary className="text-muted" style={{ cursor: 'pointer', userSelect: 'none' }}>
              <small>Show additional statistics →</small>
            </summary>
            <Row className="mt-3">
              <Col md={2} sm={4} xs={6}><StatCard title="Red Wavelengths" value={stats.uniquernm} /></Col>
              <Col md={2} sm={4} xs={6}><StatCard title="Green Wavelengths" value={stats.uniquegnm} /></Col>
              <Col md={2} sm={4} xs={6}><StatCard title="Blue Wavelengths" value={stats.uniquebnm} /></Col>
              <Col md={2} sm={4} xs={6}><StatCard title="Red mcd" value={stats.uniquermcd} /></Col>
              <Col md={2} sm={4} xs={6}><StatCard title="Green mcd" value={stats.uniquegmcd} /></Col>
              <Col md={2} sm={4} xs={6}><StatCard title="Blue mcd" value={stats.uniquebmcd} /></Col>
              <Col md={2} sm={4} xs={6}><StatCard title="Red V" value={stats.uniquerv} /></Col>
              <Col md={2} sm={4} xs={6}><StatCard title="Green V" value={stats.uniquegv} /></Col>
              <Col md={2} sm={4} xs={6}><StatCard title="Blue V" value={stats.uniquebv} /></Col>
            </Row>
          </details>
        </Col>
      </Row>

      {/* Tables with Tabs */}
      <Tab.Container defaultActiveKey="tile-types">
        <Card>
          <Card.Header className="bg-white">
            <Nav variant="tabs">
              <Nav.Item>
                <Nav.Link eventKey="tile-types">
                  Tile Types <Badge bg="secondary">{prefixData.length}</Badge>
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="tile-types-2">
                  Tile Types #2 <Badge bg="secondary">{tileTypesInfo4Data.length}</Badge>
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="orders">
                  Orders <Badge bg="secondary">{orderData.length}</Badge>
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="years">
                  By Year <Badge bg="secondary">{yearData.length}</Badge>
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="led-batches">
                  LED Batches <Badge bg="secondary">{ledBatchData.length}</Badge>
                </Nav.Link>
              </Nav.Item>
            </Nav>
          </Card.Header>
          <Card.Body>
            <Tab.Content>
              {/* Tile Types Tab */}
              <Tab.Pane eventKey="tile-types">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <Form.Control
                    type="text"
                    placeholder="Search tile types..."
                    value={tileFilter}
                    onChange={(e) => setTileFilter(e.target.value)}
                    style={{ maxWidth: '300px' }}
                  />
                  <small className="text-muted">
                    {tileTable.getFilteredRowModel().rows.length} results
                    {tileFilter && ` (filtered from ${prefixData.length})`}
                  </small>
                </div>

                <div className="table-responsive">
                  <Table striped bordered hover size="sm">
                    <thead>
                      {tileTable.getHeaderGroups().map(headerGroup => (
                        <tr key={headerGroup.id}>
                          {headerGroup.headers.map(header => (
                            <th key={header.id} style={{ cursor: header.column.getCanSort() ? 'pointer' : 'default' }}>
                              <div
                                onClick={header.column.getToggleSortingHandler()}
                                className="d-flex justify-content-between align-items-center"
                              >
                                {flexRender(header.column.columnDef.header, header.getContext())}
                                {header.column.getCanSort() && (
                                  <span className="ms-1">
                                    {{
                                      asc: '🔼',
                                      desc: '🔽',
                                    }[header.column.getIsSorted()] ?? '↕️'}
                                  </span>
                                )}
                              </div>
                            </th>
                          ))}
                        </tr>
                      ))}
                    </thead>
                    <tbody>
                      {tileTable.getRowModel().rows.map(row => (
                        <tr key={row.id}>
                          {row.getVisibleCells().map(cell => (
                            <td key={cell.id}>
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>

                <PaginationControls table={tileTable} />
              </Tab.Pane>

              {/* Tile Types #2 Tab (from ROEMainInfo.info4) */}
              <Tab.Pane eventKey="tile-types-2">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <Form.Control
                    type="text"
                    placeholder="Search tile types (info4)..."
                    value={tileTypesInfo4Filter}
                    onChange={(e) => setTileTypesInfo4Filter(e.target.value)}
                    style={{ maxWidth: '300px' }}
                  />
                  <small className="text-muted">
                    {tileTypesInfo4Table.getFilteredRowModel().rows.length} results
                    {tileTypesInfo4Filter && ` (filtered from ${tileTypesInfo4Data.length})`}
                  </small>
                </div>

                <div className="table-responsive">
                  <Table striped bordered hover size="sm">
                    <thead>
                      {tileTypesInfo4Table.getHeaderGroups().map(headerGroup => (
                        <tr key={headerGroup.id}>
                          {headerGroup.headers.map(header => (
                            <th key={header.id} style={{ cursor: header.column.getCanSort() ? 'pointer' : 'default' }}>
                              <div
                                onClick={header.column.getToggleSortingHandler()}
                                className="d-flex justify-content-between align-items-center"
                              >
                                {flexRender(header.column.columnDef.header, header.getContext())}
                                {header.column.getCanSort() && (
                                  <span className="ms-1">
                                    {{
                                      asc: '🔼',
                                      desc: '🔽',
                                    }[header.column.getIsSorted()] ?? '↕️'}
                                  </span>
                                )}
                              </div>
                            </th>
                          ))}
                        </tr>
                      ))}
                    </thead>
                    <tbody>
                      {tileTypesInfo4Table.getRowModel().rows.map(row => (
                        <tr key={row.id}>
                          {row.getVisibleCells().map(cell => (
                            <td key={cell.id}>
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>

                <PaginationControls table={tileTypesInfo4Table} />
              </Tab.Pane>

              {/* Orders Tab */}
              <Tab.Pane eventKey="orders">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <Form.Control
                    type="text"
                    placeholder="Search orders..."
                    value={orderFilter}
                    onChange={(e) => setOrderFilter(e.target.value)}
                    style={{ maxWidth: '300px' }}
                  />
                  <small className="text-muted">
                    {orderTable.getFilteredRowModel().rows.length} results
                    {orderFilter && ` (filtered from ${orderData.length})`}
                  </small>
                </div>

                <div className="table-responsive">
                  <Table striped bordered hover size="sm">
                    <thead>
                      {orderTable.getHeaderGroups().map(headerGroup => (
                        <tr key={headerGroup.id}>
                          {headerGroup.headers.map(header => (
                            <th key={header.id} style={{ cursor: header.column.getCanSort() ? 'pointer' : 'default' }}>
                              <div
                                onClick={header.column.getToggleSortingHandler()}
                                className="d-flex justify-content-between align-items-center"
                              >
                                {flexRender(header.column.columnDef.header, header.getContext())}
                                {header.column.getCanSort() && (
                                  <span className="ms-1">
                                    {{
                                      asc: '🔼',
                                      desc: '🔽',
                                    }[header.column.getIsSorted()] ?? '↕️'}
                                  </span>
                                )}
                              </div>
                            </th>
                          ))}
                        </tr>
                      ))}
                    </thead>
                    <tbody>
                      {orderTable.getRowModel().rows.map(row => (
                        <tr key={row.id}>
                          {row.getVisibleCells().map(cell => (
                            <td key={cell.id}>
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>

                <PaginationControls table={orderTable} />
              </Tab.Pane>

              {/* Year Statistics Tab */}
              <Tab.Pane eventKey="years">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <Form.Control
                    type="text"
                    placeholder="Search years..."
                    value={yearFilter}
                    onChange={(e) => setYearFilter(e.target.value)}
                    style={{ maxWidth: '300px' }}
                  />
                  <small className="text-muted">
                    {yearTable.getFilteredRowModel().rows.length} results
                    {yearFilter && ` (filtered from ${yearData.length})`}
                  </small>
                </div>

                <div className="table-responsive">
                  <Table striped bordered hover size="sm">
                    <thead>
                      {yearTable.getHeaderGroups().map(headerGroup => (
                        <tr key={headerGroup.id}>
                          {headerGroup.headers.map(header => (
                            <th key={header.id} style={{ cursor: header.column.getCanSort() ? 'pointer' : 'default' }}>
                              <div
                                onClick={header.column.getToggleSortingHandler()}
                                className="d-flex justify-content-between align-items-center"
                              >
                                {flexRender(header.column.columnDef.header, header.getContext())}
                                {header.column.getCanSort() && (
                                  <span className="ms-1">
                                    {{
                                      asc: '🔼',
                                      desc: '🔽',
                                    }[header.column.getIsSorted()] ?? '↕️'}
                                  </span>
                                )}
                              </div>
                            </th>
                          ))}
                        </tr>
                      ))}
                    </thead>
                    <tbody>
                      {yearTable.getRowModel().rows.map(row => (
                        <tr key={row.id}>
                          {row.getVisibleCells().map(cell => (
                            <td key={cell.id}>
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>

                <PaginationControls table={yearTable} />
              </Tab.Pane>

              {/* LED Batches Tab */}
              <Tab.Pane eventKey="led-batches">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <Form.Control
                    type="text"
                    placeholder="Search LED batches..."
                    value={ledBatchFilter}
                    onChange={(e) => setLedBatchFilter(e.target.value)}
                    style={{ maxWidth: '300px' }}
                  />
                  <small className="text-muted">
                    {ledBatchTable.getFilteredRowModel().rows.length} results
                    {ledBatchFilter && ` (filtered from ${ledBatchData.length})`}
                  </small>
                </div>

                <div className="table-responsive">
                  <Table striped bordered hover size="sm">
                    <thead>
                      {ledBatchTable.getHeaderGroups().map(headerGroup => (
                        <tr key={headerGroup.id}>
                          {headerGroup.headers.map(header => (
                            <th
                              key={header.id}
                              onClick={header.column.getToggleSortingHandler()}
                              style={{ cursor: 'pointer' }}
                            >
                              <div className="d-flex justify-content-between align-items-center">
                                {flexRender(header.column.columnDef.header, header.getContext())}
                                {header.column.getCanSort() && (
                                  <span>
                                    {{
                                      asc: '🔼',
                                      desc: '🔽',
                                    }[header.column.getIsSorted()] ?? '↕️'}
                                  </span>
                                )}
                              </div>
                            </th>
                          ))}
                        </tr>
                      ))}
                    </thead>
                    <tbody>
                      {ledBatchTable.getRowModel().rows.map(row => (
                        <tr key={row.id}>
                          {row.getVisibleCells().map(cell => (
                            <td key={cell.id}>
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>

                <PaginationControls table={ledBatchTable} />
              </Tab.Pane>
            </Tab.Content>
          </Card.Body>
        </Card>
      </Tab.Container>
    </Container>
  );
}

export default Statistics;
